import React, { useEffect } from 'react';
import { IntlProvider } from 'react-intl-next';

import {
  SmartCard,
  SmartCardProvider,
  SmartCardAppearance,
} from '@trello/smart-card';

import FabricAnalyticsListeners from '@atlaskit/analytics-listeners';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { Analytics } from '@trello/atlassian-analytics';

import {
  objectResolverClient,
  ResolveResponse,
} from '@trello/smart-card/object-resolver-client';
import { smartCardClient } from '@trello/smart-card/smart-card-client';

import { useLanguage } from '@trello/locale';
import { useAkMediaLocale } from './useAkMediaLocale';

interface AnalyticsContextDataType {
  attributes: {
    component?: string;
    definitionId?: string;
    accessType?: string;
    cloudId?: string;
    urlType?: string;
  };
}

export interface AkSmartCardProps {
  appearance: SmartCardAppearance;
  inheritDimensions: boolean;
  isFrameVisible?: boolean;
  showActions: boolean;
}

interface Props {
  url: string;
  plainLink: () => JSX.Element;
  akSmartCardProps: AkSmartCardProps;
  onLinkResolved?: (resolved: ResolveResponse) => void;
  onLinkUnresolved?: () => void;
  onSmartCardClick?: () => void;
  shouldRender: boolean;
  analyticsContextData: AnalyticsContextDataType;
  akSmartCardProviderKey: number;
}

export const AkSmartCardWithProvider = ({
  url,
  plainLink,
  onSmartCardClick,
  onLinkResolved,
  onLinkUnresolved,
  akSmartCardProps,
  shouldRender = false,
  analyticsContextData,
  akSmartCardProviderKey,
}: Props) => {
  const { language } = useLanguage();
  const { messages, isLocaleReady: isAkMediaLocaleReady } = useAkMediaLocale();

  useEffect(() => {
    const checkIfSmartCard = async () => {
      const resolved = await objectResolverClient.resolveUrl(url, {
        sourceComponent: 'atlaskit-smart-card',
      });

      if (resolved) {
        onLinkResolved?.(resolved);
      } else {
        onLinkUnresolved?.();
      }
    };

    checkIfSmartCard();
  }, [url, onLinkResolved, onLinkUnresolved]);

  return shouldRender && isAkMediaLocaleReady ? (
    <FabricAnalyticsListeners
      client={Analytics.dangerouslyGetAnalyticsWebClient()}
    >
      <AnalyticsContext data={analyticsContextData}>
        <SmartCardProvider
          client={smartCardClient}
          key={akSmartCardProviderKey}
        >
          <IntlProvider locale={language} messages={messages}>
            <SmartCard
              // eslint-disable-next-line react/jsx-no-bind
              onClick={(e) => {
                // This is needed to prevent the click event from propagating up to
                // cardView.handleLinkInCardNameClick.
                e.preventDefault();
                e.stopPropagation();
                window.open(url, '_blank');
                onSmartCardClick?.();
              }}
              url={url}
              {...akSmartCardProps}
              platform="web"
            />
          </IntlProvider>
        </SmartCardProvider>
      </AnalyticsContext>
    </FabricAnalyticsListeners>
  ) : (
    plainLink()
  );
};
